import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MobileNavService } from '../../../shared/mobile/mobile-bottom-nav/mobile-nav.service';
import { MobilePageShellComponent } from '../../../shared/mobile/mobile-page-shell/mobile-page-shell.component';
import { MobileMetricRibbonComponent, MobileMetric } from '../../../shared/mobile/mobile-metric-ribbon/mobile-metric-ribbon.component';
import { MobileDataCardComponent, MobileCardField } from '../../../shared/mobile/mobile-data-card/mobile-data-card.component';
import { PieChartComponent } from '../../../shared/reusables/pie-chart/pie-chart.component';
import { BarChartComponent } from '../../../shared/reusables/bar-chart/bar-chart.component';
import { EnhancedItemDTO, CategoryDTO, UnitOfMeasureDTO, SupplierDTO } from '../inventory-dashboard.component';
import { InventoryItemOffcanvasComponent } from '../inventory-item-offcanvas/inventory-item-offcanvas.component';
import { SingleSelectDropdownComponent } from '../../business-details/single-select-dropdown.component';
import { OffcanvasSheetComponent } from '../../shared/offcanvas-sheet/offcanvas-sheet.component';

@Component({
  selector: 'app-mobile-inventory-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MobilePageShellComponent,
    MobileMetricRibbonComponent,
    MobileDataCardComponent,
    PieChartComponent,
    BarChartComponent,
    InventoryItemOffcanvasComponent,
    SingleSelectDropdownComponent,
    OffcanvasSheetComponent,
  ],
  templateUrl: './mobile-inventory-view.component.html',
  styleUrls: [
    '../../shared/offcanvas-sheet/offcanvas-sheet.shared.css',
    './mobile-inventory-view.component.css'
  ]
})
export class MobileInventoryViewComponent implements OnInit, OnDestroy {
  // Metrics
  @Input() totalRawMaterials = 0;
  @Input() totalFinishedGoods = 0;
  @Input() lowStockItems = 0;
  @Input() totalRawMaterialValue = 0;
  @Input() totalProductValueFromBOM = 0;

  // Data
  @Input() rawMaterials: EnhancedItemDTO[] = [];
  @Input() filteredFinishedGoods: EnhancedItemDTO[] = [];

  // Lookup data for offcanvas
  @Input() categories: CategoryDTO[] = [];
  @Input() unitsOfMeasure: UnitOfMeasureDTO[] = [];
  @Input() suppliers: SupplierDTO[] = [];

  // Chart data
  @Input() stackedBarData: any = null;
  @Input() baseColors: string[] = [];
  @Input() categoryLabels: string[] = [];
  @Input() categoryData: number[] = [];
  @Input() ItemsPerPage = 10;
  @Input() filters: { label: string; key: string }[] = [];
  @Input() selectedFilter = 'all';
  @Input() currentPage = 1;
  @Input() totalPages = 1;
  @Input() totalItemsCount = 0;
  @Input() goToPage: (page: number) => void = () => {};
  @Input() onPageSizeChange: (size: number) => void = () => {};

  // Outputs
  @Output() saveItemMobile = new EventEmitter<any>();
  @Output() deleteItemMobile = new EventEmitter<{ item: EnhancedItemDTO; mode: 'raw' | 'product' }>();
  @Output() filterChange = new EventEmitter<string>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() downloadTemplateMobile = new EventEmitter<void>();
  @Output() downloadFinishedTemplateMobile = new EventEmitter<void>();
  @Output() importMobile = new EventEmitter<Event>();
  @Output() createCategoryMobile = new EventEmitter<string>();

  mobileActiveTab = 'raw';
  mobileView: 'overview' | 'inventory' | 'charts' | 'import' = 'overview';

  readonly Math = Math;
  readonly String = String;

  toOption = (id: number | null | undefined, label: string) => ({ value: String(id ?? ''), label });
  fromValue = (v: string | null) => (v == null || v === '' ? null : Number(v));

  get barChartWrapHeight(): string {
    const n = this.stackedBarData?.labels?.length ?? 0;
    const px = Math.max(260, 80 + n * 44);
    return `${px}px`;
  }

  get filterCategoryOptions() { return this.categories.map(c => this.toOption(c.categoryId, c.categoryName)); }
  get filterUomOptions() { return this.unitsOfMeasure.map(u => this.toOption(u.uomid, u.uomName)); }

  // ── Filter panel visibility ──────────────────────────────────────────────────
  mobileRawFiltersOpen = false;
  mobileProductFiltersOpen = false;

  toggleRawFilters(): void { this.mobileRawFiltersOpen = !this.mobileRawFiltersOpen; }
  toggleProductFilters(): void { this.mobileProductFiltersOpen = !this.mobileProductFiltersOpen; }

  get rawActiveFilterCount(): number {
    let count = 0;
    if (this.mobileRawSearch.trim()) count++;
    if (this.mobileRawCategoryId !== null) count++;
    if (this.mobileRawUomId !== null) count++;
    if (this.mobileRawStatus !== 'all') count++;
    if (this.mobileRawSortKey !== 'name') count++;
    return count;
  }

  get productActiveFilterCount(): number {
    let count = 0;
    if (this.mobileProductSearch.trim()) count++;
    if (this.mobileProductCategoryId !== null) count++;
    if (this.mobileProductStatus !== 'all') count++;
    if (this.mobileProductSortKey !== 'name') count++;
    return count;
  }

  clearRawFilters(): void {
    this.mobileRawSearch = '';
    this.mobileRawCategoryId = null;
    this.mobileRawUomId = null;
    this.mobileRawStatus = 'all';
    this.mobileRawSortKey = 'name';
    this.mobileRawSortAsc = true;
    this.onRawFilterChange();
  }

  clearProductFilters(): void {
    this.mobileProductSearch = '';
    this.mobileProductCategoryId = null;
    this.mobileProductStatus = 'all';
    this.mobileProductSortKey = 'name';
    this.mobileProductSortAsc = true;
    this.onProductFilterChange();
  }

  onMobileFileSelected(ev: Event): void {
    this.importMobile.emit(ev);
  }

  // ── Filter / sort state ──────────────────────────────────────────────────────
  mobileRawSearch = '';
  mobileRawCategoryId: number | null = null;
  mobileRawUomId: number | null = null;
  mobileRawStatus: 'all' | 'low' | 'in' = 'all';
  mobileRawSortKey: 'name' | 'qty' | 'value' = 'name';
  mobileRawSortAsc = true;

  mobileProductSearch = '';
  mobileProductCategoryId: number | null = null;
  mobileProductStatus: 'all' | 'low' | 'in' = 'all';
  mobileProductSortKey: 'name' | 'qty' | 'price' = 'name';
  mobileProductSortAsc = true;

  // ── Add choice sheet state ───────────────────────────────────────────────────
  mobileAddChoiceVisible = false;
  mobileAddChoiceClosing = false;
  mobileAddChoiceMode: 'raw' | 'product' = 'raw';
  mobileAddChoiceSwipeDeltaY = 0;
  mobileAddChoiceSwipeProgress = 0;
  mobileAddChoiceSwipeActive = false;
  private mobileAddChoiceSwipeStartY = 0;
  private mobileAddChoiceSwipeSheetHeight = 0;

  // ── Item picker sheet state ──────────────────────────────────────────────────
  mobileItemPickerVisible = false;
  mobileItemPickerClosing = false;
  mobileItemPickerSearch = '';
  mobileItemPickerSwipeDeltaY = 0;
  mobileItemPickerSwipeProgress = 0;
  mobileItemPickerSwipeActive = false;
  private mobileItemPickerSwipeStartY = 0;
  private mobileItemPickerSwipeSheetHeight = 0;

  // ── Category create sheet state ──────────────────────────────────────────────
  // Rendered via the shared <app-offcanvas-sheet>, which owns the backdrop,
  // swipe-to-dismiss and slide animations.
  mobileCategoryCreateVisible = false;
  newCategoryName = '';

  // ── Offcanvas state ──────────────────────────────────────────────────────────
  mobileOffcanvasVisible = false;
  mobileOffcanvasMode: 'raw' | 'product' = 'raw';
  mobileOffcanvasItem: EnhancedItemDTO | null = null;
  mobileOffcanvasIsEditing = false;
  mobileOffcanvasIsAddingQuantity = false;
  mobileOffcanvasSelectedItemForQty: EnhancedItemDTO | null = null;

  // ── Delete sheet state ───────────────────────────────────────────────────────
  mobileDeleteVisible = false;
  mobileDeleteItem: EnhancedItemDTO | null = null;
  mobileDeleteMode: 'raw' | 'product' = 'raw';
  mobileDeleteClosing = false;
  mobileDeleteSwipeDeltaY = 0;
  mobileDeleteSwipeProgress = 0;
  mobileDeleteSwipeActive = false;
  private mobileDeleteSwipeStartY = 0;
  private mobileDeleteSwipeSheetHeight = 0;

  // ── Low-stock sheet state ────────────────────────────────────────────────────
  mobileLowStockVisible = false;
  mobileLowStockClosing = false;
  mobileLowStockSwipeDeltaY = 0;
  mobileLowStockSwipeProgress = 0;
  mobileLowStockSwipeActive = false;
  private mobileLowStockSwipeStartY = 0;
  private mobileLowStockSwipeSheetHeight = 0;

  // ── Pagination ───────────────────────────────────────────────────────────────
  rawMaterialsCurrentPage = 1;
  rawMaterialsPageSize = 10;
  finishedGoodsCurrentPage = 1;
  finishedGoodsPageSize = 10;

  private subs: Subscription[] = [];
  private currencyPipe = new CurrencyPipe('en-ZA');

  constructor(
    private mobileNavService: MobileNavService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.mobileNavService.activeTab.subscribe(tab => {
        this.mobileActiveTab = tab;
        this.updateBadges();
      }),
      this.route.queryParams.subscribe(params => {
        if (params['view'] === 'charts') {
          this.mobileView = 'charts';
          this.mobileNavService.hideNav();
        } else if (params['view'] === 'tables') {
          this.mobileView = 'inventory';
          this.mobileNavService.showNav();
        } else if (params['view'] === 'import') {
          this.mobileView = 'import';
          this.mobileNavService.hideNav();
        } else {
          this.mobileView = 'overview';
          this.mobileNavService.hideNav();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  private updateBadges(): void {
    this.mobileNavService.updateBadges({
      raw: this.rawMaterials.length,
      products: this.filteredFinishedGoods.length,
    });
  }

  // ── Metrics ──────────────────────────────────────────────────────────────────
  get metricItems(): MobileMetric[] {
    const fmt = (val: number) =>
      this.currencyPipe.transform(val, 'ZAR', 'symbol', '1.0-0') ?? '';
    return [
      { label: 'Raw Materials', value: this.totalRawMaterials },
      { label: 'Products', value: this.totalFinishedGoods },
      { label: 'Low Stock', value: this.lowStockItems, warning: this.lowStockItems > 0 },
      { label: 'Material Val', value: fmt(this.totalRawMaterialValue) },
      { label: 'Product Val', value: fmt(this.totalProductValueFromBOM) },
      { label: 'Total', value: fmt(this.totalRawMaterialValue + this.totalProductValueFromBOM) },
    ];
  }

  // ── Filtered getters ─────────────────────────────────────────────────────────
  get filteredRawMaterialsMobile(): EnhancedItemDTO[] {
    let items = this.rawMaterials;
    const q = this.mobileRawSearch.toLowerCase().trim();
    if (q) {
      items = items.filter(i =>
        (i.itemName ?? '').toLowerCase().includes(q) ||
        (i.itemCode ?? '').toLowerCase().includes(q) ||
        (i.CategoryName ?? '').toLowerCase().includes(q)
      );
    }
    if (this.mobileRawCategoryId !== null) {
      items = items.filter(i => i.categoryID === this.mobileRawCategoryId);
    }
    if (this.mobileRawUomId !== null) {
      items = items.filter(i => i.purchaseUOMID === this.mobileRawUomId);
    }
    if (this.mobileRawStatus === 'low') {
      items = items.filter(i => (i.quantity ?? 0) <= (i.lowStockThreshold ?? 0));
    } else if (this.mobileRawStatus === 'in') {
      items = items.filter(i => (i.quantity ?? 0) > (i.lowStockThreshold ?? 0));
    }
    return [...items].sort((a, b) => {
      let diff = 0;
      if (this.mobileRawSortKey === 'name') diff = (a.itemName ?? '').localeCompare(b.itemName ?? '');
      else if (this.mobileRawSortKey === 'qty') diff = (a.quantity ?? 0) - (b.quantity ?? 0);
      else if (this.mobileRawSortKey === 'value') {
        const va = a.purchaseQuantity > 0 ? a.purchasePrice / a.purchaseQuantity : a.purchasePrice;
        const vb = b.purchaseQuantity > 0 ? b.purchasePrice / b.purchaseQuantity : b.purchasePrice;
        diff = va - vb;
      }
      return this.mobileRawSortAsc ? diff : -diff;
    });
  }

  get filteredProductsMobile(): EnhancedItemDTO[] {
    let items = this.filteredFinishedGoods;
    const q = this.mobileProductSearch.toLowerCase().trim();
    if (q) {
      items = items.filter(i =>
        (i.itemName ?? '').toLowerCase().includes(q) ||
        (i.itemCode ?? '').toLowerCase().includes(q) ||
        (i.CategoryName ?? '').toLowerCase().includes(q)
      );
    }
    if (this.mobileProductCategoryId !== null) {
      items = items.filter(i => i.categoryID === this.mobileProductCategoryId);
    }
    if (this.mobileProductStatus === 'low') {
      items = items.filter(i => (i.quantity ?? 0) <= (i.lowStockThreshold ?? 0));
    } else if (this.mobileProductStatus === 'in') {
      items = items.filter(i => (i.quantity ?? 0) > (i.lowStockThreshold ?? 0));
    }
    return [...items].sort((a, b) => {
      let diff = 0;
      if (this.mobileProductSortKey === 'name') diff = (a.itemName ?? '').localeCompare(b.itemName ?? '');
      else if (this.mobileProductSortKey === 'qty') diff = (a.quantity ?? 0) - (b.quantity ?? 0);
      else if (this.mobileProductSortKey === 'price') diff = (a.sellingPrice ?? 0) - (b.sellingPrice ?? 0);
      return this.mobileProductSortAsc ? diff : -diff;
    });
  }

  get lowStockItemsCombined(): EnhancedItemDTO[] {
    return [
      ...this.rawMaterials.filter(i => (i.quantity ?? 0) <= (i.lowStockThreshold ?? 0)),
      ...this.filteredFinishedGoods.filter(i => (i.quantity ?? 0) <= (i.lowStockThreshold ?? 0)),
    ];
  }

  // ── Paged slices ─────────────────────────────────────────────────────────────
  get rawMaterialsPaged(): EnhancedItemDTO[] {
    const src = this.filteredRawMaterialsMobile;
    const start = (this.rawMaterialsCurrentPage - 1) * this.rawMaterialsPageSize;
    return src.slice(start, start + this.rawMaterialsPageSize);
  }

  get finishedGoodsPaged(): EnhancedItemDTO[] {
    const src = this.filteredProductsMobile;
    const start = (this.finishedGoodsCurrentPage - 1) * this.finishedGoodsPageSize;
    return src.slice(start, start + this.finishedGoodsPageSize);
  }

  get rawMaterialsTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRawMaterialsMobile.length / this.rawMaterialsPageSize));
  }

  get finishedGoodsTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredProductsMobile.length / this.finishedGoodsPageSize));
  }

  changeRawPage(page: number): void {
    if (page >= 1 && page <= this.rawMaterialsTotalPages) this.rawMaterialsCurrentPage = page;
  }

  changeFinishedPage(page: number): void {
    if (page >= 1 && page <= this.finishedGoodsTotalPages) this.finishedGoodsCurrentPage = page;
  }

  onRawFilterChange(): void { this.rawMaterialsCurrentPage = 1; }
  onProductFilterChange(): void { this.finishedGoodsCurrentPage = 1; }

  cycleSortRaw(key: 'name' | 'qty' | 'value'): void {
    if (this.mobileRawSortKey === key) { this.mobileRawSortAsc = !this.mobileRawSortAsc; }
    else { this.mobileRawSortKey = key; this.mobileRawSortAsc = true; }
    this.rawMaterialsCurrentPage = 1;
  }

  cycleSortProduct(key: 'name' | 'qty' | 'price'): void {
    if (this.mobileProductSortKey === key) { this.mobileProductSortAsc = !this.mobileProductSortAsc; }
    else { this.mobileProductSortKey = key; this.mobileProductSortAsc = true; }
    this.finishedGoodsCurrentPage = 1;
  }

  // ── Card field helpers ────────────────────────────────────────────────────────
  rawFields(item: EnhancedItemDTO): MobileCardField[] {
    const unitCost = item.purchaseQuantity > 0
      ? item.purchasePrice / item.purchaseQuantity
      : item.purchasePrice;
    const isLow = (item.quantity ?? 0) <= (item.lowStockThreshold ?? 0);
    return [
      { label: 'SKU', value: item.itemCode ?? '' },
      { label: 'Category', value: item.CategoryName ?? '', chip: true },
      { label: 'Quantity', value: (item.quantity ?? 0).toFixed(2), chip: true, tone: isLow ? 'danger' : 'success' },
      { label: 'UOM', value: this.getUomCode(item.purchaseUOMID) },
      { label: 'Unit Cost', value: `R ${unitCost.toFixed(2)}` },
      { label: 'Purchase Price', value: `R ${item.purchasePrice?.toFixed(2) ?? '0.00'}` },
      { label: 'Reorder Level', value: item.lowStockThreshold ?? 0 },
    ];
  }

  productFields(item: EnhancedItemDTO): MobileCardField[] {
    const isLow = (item.quantity ?? 0) <= (item.lowStockThreshold ?? 0);
    return [
      { label: 'SKU', value: item.itemCode ?? '' },
      { label: 'Category', value: item.CategoryName ?? '', chip: true },
      { label: 'Quantity', value: (item.quantity ?? 0).toFixed(2), chip: true, tone: isLow ? 'danger' : 'success' },
      { label: 'Selling Price', value: `R ${item.sellingPrice?.toFixed(2) ?? '0.00'}` },
      { label: 'Reorder Level', value: item.lowStockThreshold ?? 0 },
    ];
  }

  getUomCode(uomId: number): string {
    return (this.unitsOfMeasure.find(u => u.uomid === uomId)?.uomCode ?? '').replace(/2$/, '²');
  }

  // ── Add choice sheet ─────────────────────────────────────────────────────────
  openMobileAddChoice(mode: 'raw' | 'product'): void {
    this.mobileAddChoiceMode = mode;
    this.mobileAddChoiceClosing = false;
    this.mobileAddChoiceSwipeDeltaY = 0;
    this.mobileAddChoiceSwipeProgress = 0;
    this.mobileAddChoiceVisible = true;
  }

  closeMobileAddChoice(): void {
    this.mobileAddChoiceClosing = true;
    this.mobileAddChoiceSwipeDeltaY = 0;
    this.mobileAddChoiceSwipeProgress = 0;
    setTimeout(() => {
      this.mobileAddChoiceClosing = false;
      this.mobileAddChoiceVisible = false;
    }, 300);
  }

  chooseAddNew(): void {
    this.closeMobileAddChoice();
    setTimeout(() => this.openAddOffcanvas(this.mobileAddChoiceMode), 320);
  }

  chooseAddQuantity(): void {
    this.closeMobileAddChoice();
    setTimeout(() => {
      this.mobileItemPickerSearch = '';
      this.mobileItemPickerClosing = false;
      this.mobileItemPickerSwipeDeltaY = 0;
      this.mobileItemPickerSwipeProgress = 0;
      this.mobileItemPickerVisible = true;
    }, 320);
  }

  closeItemPicker(): void {
    this.mobileItemPickerClosing = true;
    this.mobileItemPickerSwipeDeltaY = 0;
    this.mobileItemPickerSwipeProgress = 0;
    setTimeout(() => {
      this.mobileItemPickerClosing = false;
      this.mobileItemPickerVisible = false;
    }, 300);
  }

  get mobileItemPickerFiltered(): EnhancedItemDTO[] {
    const src = this.mobileAddChoiceMode === 'raw' ? this.rawMaterials : this.filteredFinishedGoods;
    const q = this.mobileItemPickerSearch.toLowerCase().trim();
    if (!q) return src;
    return src.filter(i =>
      (i.itemName ?? '').toLowerCase().includes(q) ||
      (i.itemCode ?? '').toLowerCase().includes(q)
    );
  }

  selectItemForQtyAdd(item: EnhancedItemDTO): void {
    this.closeItemPicker();
    setTimeout(() => {
      this.mobileOffcanvasMode = this.mobileAddChoiceMode;
      this.mobileOffcanvasIsAddingQuantity = true;
      this.mobileOffcanvasSelectedItemForQty = item;
      this.mobileOffcanvasItem = null;
      this.mobileOffcanvasIsEditing = false;
      this.mobileOffcanvasVisible = true;
    }, 320);
  }

  onAddChoiceTouchStart(e: TouchEvent): void {
    this.mobileAddChoiceSwipeActive = true;
    this.mobileAddChoiceSwipeStartY = e.touches[0].clientY;
    const sheet = (e.currentTarget as HTMLElement).closest('.m-choice-sheet') as HTMLElement;
    this.mobileAddChoiceSwipeSheetHeight = sheet?.offsetHeight ?? 300;
    this.mobileAddChoiceSwipeDeltaY = 0;
    this.mobileAddChoiceSwipeProgress = 0;
  }

  onAddChoiceTouchMove(e: TouchEvent): void {
    if (!this.mobileAddChoiceSwipeActive) return;
    this.mobileAddChoiceSwipeDeltaY = Math.max(0, e.touches[0].clientY - this.mobileAddChoiceSwipeStartY);
    this.mobileAddChoiceSwipeProgress = this.mobileAddChoiceSwipeSheetHeight > 0
      ? Math.min(1, this.mobileAddChoiceSwipeDeltaY / (this.mobileAddChoiceSwipeSheetHeight * 0.3)) : 0;
  }

  onAddChoiceTouchEnd(): void {
    if (!this.mobileAddChoiceSwipeActive) return;
    this.mobileAddChoiceSwipeActive = false;
    if (this.mobileAddChoiceSwipeDeltaY > this.mobileAddChoiceSwipeSheetHeight * 0.3) {
      this.closeMobileAddChoice();
    } else {
      this.mobileAddChoiceSwipeDeltaY = 0;
      this.mobileAddChoiceSwipeProgress = 0;
    }
  }

  onItemPickerTouchStart(e: TouchEvent): void {
    this.mobileItemPickerSwipeActive = true;
    this.mobileItemPickerSwipeStartY = e.touches[0].clientY;
    const sheet = (e.currentTarget as HTMLElement).closest('.m-picker-sheet') as HTMLElement;
    this.mobileItemPickerSwipeSheetHeight = sheet?.offsetHeight ?? 300;
    this.mobileItemPickerSwipeDeltaY = 0;
    this.mobileItemPickerSwipeProgress = 0;
  }

  onItemPickerTouchMove(e: TouchEvent): void {
    if (!this.mobileItemPickerSwipeActive) return;
    this.mobileItemPickerSwipeDeltaY = Math.max(0, e.touches[0].clientY - this.mobileItemPickerSwipeStartY);
    this.mobileItemPickerSwipeProgress = this.mobileItemPickerSwipeSheetHeight > 0
      ? Math.min(1, this.mobileItemPickerSwipeDeltaY / (this.mobileItemPickerSwipeSheetHeight * 0.3)) : 0;
  }

  onItemPickerTouchEnd(): void {
    if (!this.mobileItemPickerSwipeActive) return;
    this.mobileItemPickerSwipeActive = false;
    if (this.mobileItemPickerSwipeDeltaY > this.mobileItemPickerSwipeSheetHeight * 0.3) {
      this.closeItemPicker();
    } else {
      this.mobileItemPickerSwipeDeltaY = 0;
      this.mobileItemPickerSwipeProgress = 0;
    }
  }

  // ── Category create sheet ─────────────────────────────────────────────────────
  openCategoryCreateSheet(): void {
    this.newCategoryName = '';
    this.mobileCategoryCreateVisible = true;
  }

  closeCategoryCreateSheet(): void {
    this.mobileCategoryCreateVisible = false;
  }

  submitNewCategory(): void {
    const name = this.newCategoryName.trim();
    if (!name) return;
    this.createCategoryMobile.emit(name);
    this.closeCategoryCreateSheet();
  }

  onOffcanvasAddNewCategory(): void {
    this.openCategoryCreateSheet();
  }

  onOffcanvasAddNewSupplier(): void {
    this.router.navigate(['/config'], { queryParams: { tab: 'suppliers', view: 'single' }, state: { from: 'inventory' } });
  }

  // ── Offcanvas ─────────────────────────────────────────────────────────────────
  openAddOffcanvas(mode: 'raw' | 'product'): void {
    this.mobileOffcanvasMode = mode;
    this.mobileOffcanvasItem = null;
    this.mobileOffcanvasIsEditing = false;
    this.mobileOffcanvasIsAddingQuantity = false;
    this.mobileOffcanvasSelectedItemForQty = null;
    this.mobileOffcanvasVisible = true;
  }

  openEditOffcanvas(item: EnhancedItemDTO, mode: 'raw' | 'product'): void {
    this.mobileOffcanvasMode = mode;
    this.mobileOffcanvasItem = item;
    this.mobileOffcanvasIsEditing = true;
    this.mobileOffcanvasVisible = true;
  }

  onOffcanvasClose(): void {
    this.mobileOffcanvasVisible = false;
    this.mobileOffcanvasItem = null;
    this.mobileOffcanvasIsAddingQuantity = false;
    this.mobileOffcanvasSelectedItemForQty = null;
  }

  onOffcanvasSave(payload: any): void {
    this.saveItemMobile.emit(payload);
  }

  // ── Delete sheet ──────────────────────────────────────────────────────────────
  openDeleteSheet(item: EnhancedItemDTO, mode: 'raw' | 'product'): void {
    this.mobileDeleteItem = item;
    this.mobileDeleteMode = mode;
    this.mobileDeleteClosing = false;
    this.mobileDeleteSwipeDeltaY = 0;
    this.mobileDeleteSwipeProgress = 0;
    this.mobileDeleteVisible = true;
  }

  closeDeleteSheet(): void {
    this.mobileDeleteClosing = true;
    this.mobileDeleteSwipeDeltaY = 0;
    this.mobileDeleteSwipeProgress = 0;
    setTimeout(() => {
      this.mobileDeleteClosing = false;
      this.mobileDeleteVisible = false;
      this.mobileDeleteItem = null;
    }, 300);
  }

  confirmDelete(): void {
    if (this.mobileDeleteItem) {
      this.deleteItemMobile.emit({ item: this.mobileDeleteItem, mode: this.mobileDeleteMode });
    }
    this.closeDeleteSheet();
  }

  onDeleteSheetTouchStart(e: TouchEvent): void {
    this.mobileDeleteSwipeActive = true;
    this.mobileDeleteSwipeStartY = e.touches[0].clientY;
    const sheet = (e.currentTarget as HTMLElement).closest('.m-del-sheet') as HTMLElement;
    this.mobileDeleteSwipeSheetHeight = sheet?.offsetHeight ?? 300;
    this.mobileDeleteSwipeDeltaY = 0;
    this.mobileDeleteSwipeProgress = 0;
  }

  onDeleteSheetTouchMove(e: TouchEvent): void {
    if (!this.mobileDeleteSwipeActive) return;
    this.mobileDeleteSwipeDeltaY = Math.max(0, e.touches[0].clientY - this.mobileDeleteSwipeStartY);
    this.mobileDeleteSwipeProgress = this.mobileDeleteSwipeSheetHeight > 0
      ? Math.min(1, this.mobileDeleteSwipeDeltaY / (this.mobileDeleteSwipeSheetHeight * 0.3))
      : 0;
  }

  onDeleteSheetTouchEnd(): void {
    if (!this.mobileDeleteSwipeActive) return;
    this.mobileDeleteSwipeActive = false;
    if (this.mobileDeleteSwipeDeltaY > this.mobileDeleteSwipeSheetHeight * 0.3) {
      this.closeDeleteSheet();
    } else {
      this.mobileDeleteSwipeDeltaY = 0;
      this.mobileDeleteSwipeProgress = 0;
    }
  }

  // ── Low-stock sheet ───────────────────────────────────────────────────────────
  openLowStockSheet(): void {
    if (this.lowStockItems === 0) return;
    this.mobileLowStockClosing = false;
    this.mobileLowStockSwipeDeltaY = 0;
    this.mobileLowStockSwipeProgress = 0;
    this.mobileLowStockVisible = true;
  }

  closeLowStockSheet(): void {
    this.mobileLowStockClosing = true;
    this.mobileLowStockSwipeDeltaY = 0;
    this.mobileLowStockSwipeProgress = 0;
    setTimeout(() => {
      this.mobileLowStockClosing = false;
      this.mobileLowStockVisible = false;
    }, 300);
  }

  openEditFromLowStock(item: EnhancedItemDTO): void {
    const mode: 'raw' | 'product' = item.itemTypeID === 2 ? 'product' : 'raw';
    this.closeLowStockSheet();
    setTimeout(() => this.openEditOffcanvas(item, mode), 320);
  }

  onLowStockSheetTouchStart(e: TouchEvent): void {
    this.mobileLowStockSwipeActive = true;
    this.mobileLowStockSwipeStartY = e.touches[0].clientY;
    const sheet = (e.currentTarget as HTMLElement).closest('.m-ls-sheet') as HTMLElement;
    this.mobileLowStockSwipeSheetHeight = sheet?.offsetHeight ?? 300;
    this.mobileLowStockSwipeDeltaY = 0;
    this.mobileLowStockSwipeProgress = 0;
  }

  onLowStockSheetTouchMove(e: TouchEvent): void {
    if (!this.mobileLowStockSwipeActive) return;
    this.mobileLowStockSwipeDeltaY = Math.max(0, e.touches[0].clientY - this.mobileLowStockSwipeStartY);
    this.mobileLowStockSwipeProgress = this.mobileLowStockSwipeSheetHeight > 0
      ? Math.min(1, this.mobileLowStockSwipeDeltaY / (this.mobileLowStockSwipeSheetHeight * 0.3))
      : 0;
  }

  onLowStockSheetTouchEnd(): void {
    if (!this.mobileLowStockSwipeActive) return;
    this.mobileLowStockSwipeActive = false;
    if (this.mobileLowStockSwipeDeltaY > this.mobileLowStockSwipeSheetHeight * 0.3) {
      this.closeLowStockSheet();
    } else {
      this.mobileLowStockSwipeDeltaY = 0;
      this.mobileLowStockSwipeProgress = 0;
    }
  }
}
